import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/landing/Logo";
import { LogOut, Users, FileText, Settings2 } from "lucide-react";
import { http } from "@/lib/admin";
import { LeadsPanel } from "@/components/admin/LeadsPanel";
import { QuotesPanel } from "@/components/admin/QuotesPanel";
import { PricingPanel } from "@/components/admin/PricingPanel";

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    http.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false)).finally(() => setChecking(false));
  }, []);

  if (checking) return <Center>Betöltés...</Center>;
  return user ? <Dashboard user={user} onLogout={() => setUser(false)} /> : <Login onLogin={setUser} />;
}

export const Center = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center text-[var(--ink-muted)]">{children}</div>
);

export function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await http.post("/auth/login", { email, password });
      localStorage.setItem("v2max_token", data.token);
      onLogin(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Sikertelen bejelentkezés");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--stone-bg2)] px-6">
      <form onSubmit={submit} className="w-full max-w-sm bg-white border border-[var(--line)] rounded-3xl p-9 shadow-sm">
        <div className="flex items-center gap-2.5 mb-7">
          <Logo className="w-8 h-8" />
          <div>
            <p className="font-serif-d font-semibold text-lg leading-none">Pilates Factory Admin</p>
            <p className="eyebrow text-[var(--ink-muted)] mt-1">Lead kezelő</p>
          </div>
        </div>
        <input required type="email" data-testid="admin-email-input" placeholder="E-mail" value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full border border-[var(--line)] rounded-xl px-4 py-3 text-sm mb-3 focus:border-[var(--terra)] outline-none" />
        <input required type="password" data-testid="admin-password-input" placeholder="Jelszó" value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full border border-[var(--line)] rounded-xl px-4 py-3 text-sm mb-4 focus:border-[var(--terra)] outline-none" />
        {error && <p data-testid="admin-login-error" className="text-red-600 text-xs mb-3">{error}</p>}
        <button type="submit" disabled={loading} data-testid="admin-login-btn"
          className="btn-terra w-full rounded-xl py-3 text-sm font-semibold disabled:opacity-60">
          {loading ? "Belépés..." : "Belépés"}
        </button>
        <Link to="/" className="block text-center eyebrow text-[var(--ink-muted)] mt-5 hover:text-[var(--terra)]">← Vissza az oldalra</Link>
      </form>
    </div>
  );
}

const TABS = [
  { key: "leads", label: "Leadek", icon: Users },
  { key: "quotes", label: "Árajánlatok", icon: FileText },
  { key: "pricing", label: "Alapárak", icon: Settings2 },
];

function Dashboard({ user, onLogout }) {
  const [tab, setTab] = useState("leads");
  const [quoteDraft, setQuoteDraft] = useState(null);

  const logout = async () => {
    localStorage.removeItem("v2max_token");
    try { await http.post("/auth/logout"); } catch {}
    onLogout();
  };

  const startQuoteFromLead = (lead) => {
    setQuoteDraft({
      lead_id: lead.id,
      name: lead.name, email: lead.email, phone: lead.phone, company: lead.company || "",
      items: [{ machine_type: lead.machine_type, quantity: lead.quantity, delivery: lead.delivery, payment: lead.payment }],
    });
    setTab("quotes");
  };

  return (
    <div className="min-h-screen bg-[var(--stone-bg2)]">
      <header className="bg-white border-b border-[var(--line)] px-6 lg:px-10 h-16 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-2.5">
          <Logo className="w-7 h-7" />
          <span className="font-serif-d font-semibold hidden sm:inline">Pilates Factory · Admin</span>
        </div>
        <nav className="flex items-center gap-1" data-testid="admin-tabs">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)}
              className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                tab === key ? "bg-[var(--ink)] text-white" : "text-[var(--ink-soft)] hover:bg-[var(--stone-bg2)]"
              }`}>
              <Icon className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-4">
          <span className="text-xs text-[var(--ink-muted)] hidden lg:block">{user.email}</span>
          <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-1.5 text-sm text-[var(--ink-soft)] hover:text-[var(--terra)]">
            <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Kilépés</span>
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10">
        {tab === "leads" && <LeadsPanel onCreateQuote={startQuoteFromLead} />}
        {tab === "quotes" && <QuotesPanel draft={quoteDraft} onDraftConsumed={() => setQuoteDraft(null)} />}
        {tab === "pricing" && <PricingPanel />}
      </div>
    </div>
  );
}
